import { useQuote } from 'components/quote_explorer/QuoteProvider'
import { DataItem } from 'components/reusable/DataDisplayComponents'
import { calculateAgeAtDate, formatMoney, fullStateNameFromAbbreviation, getFirstName } from 'utils'
import { type MemberOnboardingMedicalPlanDisplayItem, type QuotingQuoteResult } from 'utils/web_api.schemas'

interface EmployeePlanSummaryProps {
  employee: QuotingQuoteResult
  plans: MemberOnboardingMedicalPlanDisplayItem[] | undefined
  monthlyAllowance: number
}

export const EmployeePlanSummary = ({
  employee,
  plans,
  monthlyAllowance,
}: EmployeePlanSummaryProps) => {
  const { quote } = useQuote()
  const employeeName = getFirstName(employee.name) || employee.name

  // Extract unique carriers from plans and sort them
  const carriers = plans ? [...new Set(plans.map(plan => plan.plan.carrier_name))].sort() : []

  const fullyCoveredPlans = plans?.filter(plan => monthlyAllowance >= (plan.total_premium.subunits / 100))

  // Extract unique plan types from plans and sort them
  const planTypes = plans ? [...new Set(plans.map(plan => plan.plan.plan_type.toUpperCase()))].sort() : []

  return (
    <dl className='grid xs:grid-cols-2 sm:grid-cols-3 gap-4 md:gap-6'>
      <DataItem
        label='Monthly Allowance'
        value={formatMoney(monthlyAllowance)}
        size='lg'
        tooltip={
          <>
            The amount <span className='sensitive'>{employeeName}</span> will received each month
            to cover health insurance costs and qualified medical expenses.
          </>
        }
      />
      <DataItem
        label='Plans Available'
        value={plans?.length ?? <div className='h-4 w-6 bg-gray-200 rounded animate-pulse' />}
        tooltip={
          <>
            The selection of plans that <span className='sensitive'>{employeeName}</span> will be able to choose from. This number may change depending on their home address at the time they enroll.
          </>
        }
      />
      <DataItem
        label='Fully Covered Plans'
        value={fullyCoveredPlans?.length ?? <div className='h-4 w-6 bg-gray-200 rounded animate-pulse' />}
        tooltip={
          <>
            The number of plans that <span className='sensitive'>{employeeName}</span> will be able to pay for entirely with their {formatMoney(monthlyAllowance)}/mo allowance, with no spend out of their own pocket.
          </>
        }
      />
      <DataItem
        label='Carriers Available'
        value={carriers || 'None'}
        tooltip="The health insurance companies that have insurance plans available in this employee's zip code. This may change based on the employee's  full home address at the time they enroll."
      />
      <DataItem
        label='Plan Types Available'
        value={planTypes || 'None'}
        tooltip={
          <>
            The types of plans that <span className='sensitive'>{employeeName}</span> will be able to choose from. This is determined based on their home address at the time they enroll.
          </>
        }
      />
      <div className='xs:col-span-2 sm:col-span-3 grid grid-cols-3 gap-4 md:gap-6'>
        <DataItem label='State' value={fullStateNameFromAbbreviation(employee.rating_area_code.substring(0, 2))} />
        <DataItem
          label='Age'
          value={calculateAgeAtDate(employee.date_of_birth, quote?.coverage_start_date ? new Date(quote.coverage_start_date) : new Date())}
          tooltip={
            quote?.coverage_start_date && (
              <>The age of <span className='sensitive'>{employeeName}</span> at the time they will enroll in health insurance.</>
            )
          }
        />
        <DataItem label='Dependents' value={employee.dependents.length} />
      </div>
    </dl>
  )
}
